import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";

const REGION = "asia-south1";

function safeEmployee() {
  try {
    const raw = localStorage.getItem("employee");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function money(value) {
  const next = Number(value || 0);
  return Number.isFinite(next) ? Math.round(next * 100) / 100 : 0;
}

function digits(value) {
  return String(value || "").replace(/\D/g, "");
}

function createTransactionNo() {
  const time = String(Date.now()).slice(-8);
  const rand = String(Math.floor(Math.random() * 10000)).padStart(4, "0");
  return `TRN${time}${rand}`;
}

function walletRole(data = {}) {
  const raw = String(data.role || data.userRole || data.type || data.userType || "")
    .trim()
    .toLowerCase();
  if (raw.includes("mentor")) return "mentor";
  if (raw.includes("artist")) return "artist";
  if (raw.includes("admin")) return "admin";
  if (raw.includes("user")) return "user";
  return raw || "user";
}

function artistWalletPhone(id, data = {}) {
  return digits(
    data.user_uid_normalized ||
      data.user_uid ||
      data.userPhone ||
      data.user_phone ||
      data.ownerPhone ||
      data.phone ||
      id
  );
}

function mentorWalletPhone(id, data = {}, artistPhoneById = new Map()) {
  const direct = digits(
    data.user_uid_normalized ||
      data.user_uid ||
      data.userPhone ||
      data.user_phone ||
      data.ownerPhone ||
      data.phone ||
      data.mobileNumber
  );
  if (direct) return direct;
  return artistPhoneById.get(String(data.artistId || id || "")) || "";
}

function scopeMatchesRole(scope, role) {
  if (scope === "all") return ["user", "artist", "mentor"].includes(role);
  if (scope === "artist_mentor") return role === "artist" || role === "mentor";
  return role === scope;
}

function normalizeBulkScope(scope = "") {
  const value = String(scope || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  const aliases = {
    user: "user",
    users: "user",
    artist: "artist",
    artists: "artist",
    mentor: "mentor",
    mentors: "mentor",
    artist_mentor: "artist_mentor",
    artists_mentors: "artist_mentor",
    artist_and_mentor: "artist_mentor",
    all: "all",
    everyone: "all",
  };
  return aliases[value] || "";
}

function bulkReason(noteType = "manual", note = "") {
  const labels = {
    manual: "Manual credit",
    welcome_bonus: "Welcome bonus",
    refund: "Refund",
    reward: "Reward credit",
    promo_credit: "Promo credit",
  };
  const normalizedType = String(noteType || "manual").trim().toLowerCase().replace(/[\s-]+/g, "_");
  const label = labels[normalizedType] || "Manual credit";
  const cleanNote = String(note || "").trim();
  if (!cleanNote) return label;
  if (cleanNote.toLowerCase() === label.toLowerCase()) return label;
  return `${label}: ${cleanNote}`;
}

function shouldUseDirectWalletFallback(err) {
  const code = String(err?.code || "").toLowerCase();
  const message = String(err?.message || "").toLowerCase();
  return (
    code.includes("functions/not-found") ||
    code.includes("functions/unavailable") ||
    code.includes("functions/internal") ||
    code.includes("functions/permission-denied") ||
    message.includes("not found") ||
    message.includes("internal") ||
    message.includes("permission")
  );
}

export function getAdminContext() {
  const emp = safeEmployee();
  return {
    docId: String(emp.docId || ""),
    name: String(emp.name || ""),
    mobile: String(emp.mobile || ""),
    role: String(emp.role || ""),
    email: String(emp.email || ""),
  };
}

async function directAdminWalletAdjustment({
  phone,
  opType,
  amount = 0,
  reason = "",
  idempotencyKey = "",
}) {
  const cleanPhone = await resolveWalletKeyToPhone(phone);
  const cleanOpType = String(opType || "").trim().toLowerCase();
  const cleanAmount = ["block", "unblock"].includes(cleanOpType) ? 0 : money(amount);
  const admin = getAdminContext();

  if (!cleanPhone) throw new Error("Valid phone is required");
  if (!["add_balance", "debit_balance", "hold_amount", "release_hold", "block", "unblock"].includes(cleanOpType)) {
    throw new Error("Invalid wallet operation");
  }
  if (!["block", "unblock"].includes(cleanOpType) && cleanAmount <= 0) {
    throw new Error("Amount must be greater than 0");
  }

  const userRef = doc(db, "users", cleanPhone);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) throw new Error("User wallet not found");

  return runTransaction(db, async (tx) => {
    const freshSnap = await tx.get(userRef);
    if (!freshSnap.exists()) throw new Error("User wallet not found");

    const data = freshSnap.data() || {};
    const before = {
      walletBalance: money(data.walletBalance),
      walletOnHold: money(data.walletOnHold),
      walletBlocked: Boolean(data.walletBlocked),
    };
    const after = { ...before };

    if (cleanOpType === "add_balance") {
      after.walletBalance = money(before.walletBalance + cleanAmount);
    } else if (cleanOpType === "debit_balance") {
      after.walletBalance = money(before.walletBalance - cleanAmount);
    } else if (cleanOpType === "hold_amount") {
      if (before.walletBalance < cleanAmount) throw new Error("Insufficient wallet balance");
      after.walletBalance = money(before.walletBalance - cleanAmount);
      after.walletOnHold = money(before.walletOnHold + cleanAmount);
    } else if (cleanOpType === "release_hold") {
      if (before.walletOnHold < cleanAmount) throw new Error("Insufficient wallet on hold");
      after.walletOnHold = money(before.walletOnHold - cleanAmount);
      after.walletBalance = money(before.walletBalance + cleanAmount);
    } else if (cleanOpType === "block") {
      after.walletBlocked = true;
    } else if (cleanOpType === "unblock") {
      after.walletBlocked = false;
    }

    tx.set(userRef, {
      walletBalance: after.walletBalance,
      walletOnHold: after.walletOnHold,
      walletBlocked: after.walletBlocked,
      walletUpdatedAt: serverTimestamp(),
      walletUpdatedBy: admin,
    }, { merge: true });

    const txRef = doc(collection(userRef, "wallet_transactions"));
    const transactionNo = createTransactionNo();
    const direction = ["debit_balance", "hold_amount", "block"].includes(cleanOpType) ? "debit" : "credit";
    tx.set(txRef, {
      transactionNo,
      trn: transactionNo,
      type: cleanOpType === "add_balance" ? "admin_add_balance" : `admin_${cleanOpType}`,
      title: cleanOpType === "add_balance" ? "Apna Artist credited wallet balance" : "Admin wallet update",
      direction,
      amount: cleanAmount,
      status: "success",
      adminNote: String(reason || ""),
      idempotencyKey: String(idempotencyKey || ""),
      sourceType: "admin_adjustment",
      settlementStatus: "",
      requestId: "",
      createdAt: serverTimestamp(),
      createdBy: admin,
      meta: {
        module: "wallet_admin",
        action: "directAdminWalletAdjustment",
        opType: cleanOpType,
      },
    });

    const auditRef = doc(collection(db, "admin_wallet_audit"));
    tx.set(auditRef, {
      adminUid: admin.docId,
      adminEmail: admin.email,
      actionType: cleanOpType,
      targetPhone: cleanPhone,
      beforeSnapshot: before,
      afterSnapshot: after,
      reason: String(reason || ""),
      createdAt: serverTimestamp(),
    });

    return {
      ok: true,
      directFallback: true,
      phone: cleanPhone,
      opType: cleanOpType,
      amount: cleanAmount,
      walletBalanceBefore: before.walletBalance,
      walletBalanceAfter: after.walletBalance,
      walletOnHoldBefore: before.walletOnHold,
      walletOnHoldAfter: after.walletOnHold,
      walletBlockedBefore: before.walletBlocked,
      walletBlockedAfter: after.walletBlocked,
      transactionId: txRef.id,
      transactionNo,
      auditId: auditRef.id,
      targetRole: walletRole(data),
    };
  });
}

async function resolveWalletKeyToPhone(input) {
  const raw = String(input || "").trim();
  const cleanDigits = digits(raw);
  if (/^\d{6,15}$/.test(cleanDigits)) return cleanDigits;

  const normalized = raw.toLowerCase();
  if (!normalized) return "";

  const usersSnap = await getDocs(collection(db, "users"));
  let matchedPhone = "";
  usersSnap.forEach((docSnap) => {
    if (matchedPhone) return;
    const data = docSnap.data() || {};
    const walletId = String(data.walletId || "").trim().toLowerCase();
    if (walletId && walletId === normalized) {
      matchedPhone = digits(data.phone || docSnap.id || "");
    }
  });
  return matchedPhone;
}

async function resolveDirectBulkPhones({ targetMode, phone, targetScope }) {
  if (String(targetMode || "").toLowerCase() === "single") {
    const cleanPhone = await resolveWalletKeyToPhone(phone);
    return cleanPhone ? [cleanPhone] : [];
  }

  const scope = normalizeBulkScope(targetScope);
  if (!scope) throw new Error("Select a valid transfer audience");

  const [usersSnap, artistsSnap, mentorsSnap] = await Promise.all([
    getDocs(collection(db, "users")),
    getDocs(collection(db, "artists")).catch(() => ({ docs: [] })),
    getDocs(collection(db, "mentors")).catch(() => ({ docs: [] })),
  ]);
  const userPhones = new Set();
  const artistPhones = new Set();
  const mentorPhones = new Set();
  const artistPhoneById = new Map();

  usersSnap.forEach((docSnap) => {
    const data = docSnap.data() || {};
    const candidate = digits(data.phone || docSnap.id || "");
    if (/^\d{6,15}$/.test(candidate)) userPhones.add(candidate);
  });

  artistsSnap.forEach((docSnap) => {
    const phoneNumber = artistWalletPhone(docSnap.id, docSnap.data() || {});
    if (!/^\d{6,15}$/.test(phoneNumber)) return;
    artistPhoneById.set(docSnap.id, phoneNumber);
    artistPhones.add(phoneNumber);
  });

  mentorsSnap.forEach((docSnap) => {
    const phoneNumber = mentorWalletPhone(docSnap.id, docSnap.data() || {}, artistPhoneById);
    if (/^\d{6,15}$/.test(phoneNumber)) mentorPhones.add(phoneNumber);
  });

  if (scope === "all") return Array.from(userPhones);
  if (scope === "artist") return Array.from(artistPhones).filter((candidate) => userPhones.has(candidate));
  if (scope === "mentor") return Array.from(mentorPhones).filter((candidate) => userPhones.has(candidate));
  if (scope === "artist_mentor") {
    return Array.from(new Set([...artistPhones, ...mentorPhones])).filter((candidate) => userPhones.has(candidate));
  }
  if (scope === "user") {
    return Array.from(userPhones).filter((candidate) => !artistPhones.has(candidate) && !mentorPhones.has(candidate));
  }

  const phones = [];
  usersSnap.forEach((docSnap) => {
    const data = docSnap.data() || {};
    const role = walletRole(data);
    if (!scopeMatchesRole(scope, role)) return;
    const candidate = digits(data.phone || docSnap.id || "");
    if (/^\d{6,15}$/.test(candidate)) phones.push(candidate);
  });
  return Array.from(new Set(phones));
}

async function directAdminBulkWalletCredit({
  targetMode,
  phone,
  targetScope,
  amount,
  noteType,
  note,
  idempotencyKey,
}) {
  const cleanAmount = money(amount);
  if (cleanAmount <= 0) throw new Error("Amount must be greater than 0");

  const phones = await resolveDirectBulkPhones({ targetMode, phone, targetScope });
  if (!phones.length) throw new Error("No matching wallets found for this transfer");

  const reason = bulkReason(noteType, note);
  const successes = [];
  const failures = [];

  for (const targetPhone of phones) {
    try {
      const result = await directAdminWalletAdjustment({
        phone: targetPhone,
        opType: "add_balance",
        amount: cleanAmount,
        reason,
        idempotencyKey: `${idempotencyKey || "direct_bulk"}_${targetPhone}`,
      });
      successes.push({ ok: true, targetPhone, ...result });
    } catch (err) {
      failures.push({
        ok: false,
        targetPhone,
        error: err?.message || "Wallet credit failed",
      });
    }
  }

  return {
    ok: failures.length === 0,
    directFallback: true,
    partial: failures.length > 0,
    targetMode,
    targetScope,
    noteType,
    reason,
    amount: cleanAmount,
    totalTargets: phones.length,
    successCount: successes.length,
    failureCount: failures.length,
    succeededSample: successes.slice(0, 25).map((row) => row.targetPhone),
    failures: failures.slice(0, 25),
  };
}

export function createIdempotencyKey(prefix = "wallet") {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now()}_${rand}`;
}

async function callWalletFn(name, payload = {}) {
  const app = getApp();
  const fns = getFunctions(app, REGION);
  const fn = httpsCallable(fns, name);
  const res = await fn({
    ...payload,
    admin: getAdminContext(),
  });
  return res?.data || {};
}

export function adminAdjustWallet({
  phone,
  opType,
  amount,
  reason,
  idempotencyKey,
}) {
  const payload = {
    phone,
    opType,
    amount: Number(amount || 0),
    reason: reason || "",
    idempotencyKey,
  };
  return directAdminWalletAdjustment(payload).catch((directErr) => (
    callWalletFn("adminAdjustWallet", payload).catch((fnErr) => {
      if (!shouldUseDirectWalletFallback(fnErr)) throw fnErr;
      throw directErr;
    })
  ));
}

export function adminBulkWalletCredit({
  targetMode,
  phone,
  targetScope,
  amount,
  noteType,
  note,
  idempotencyKey,
}) {
  const payload = {
    targetMode,
    phone: phone || "",
    targetScope: targetScope || "",
    amount: Number(amount || 0),
    noteType: noteType || "manual",
    note: note || "",
    idempotencyKey,
  };
  return directAdminBulkWalletCredit(payload).catch((directErr) => (
    callWalletFn("adminBulkWalletCredit", payload).catch((fnErr) => {
      if (!shouldUseDirectWalletFallback(fnErr)) throw fnErr;
      throw directErr;
    })
  ));
}

export function adminUpdateSettlement({
  requestId,
  action,
  payload,
  idempotencyKey,
}) {
  return callWalletFn("adminUpdateSettlement", {
    requestId,
    action,
    payload: payload || {},
    idempotencyKey,
  });
}

export function adminWalletAddNote({
  phone,
  note,
}) {
  return callWalletFn("adminWalletAddNote", {
    phone,
    note: note || "",
  });
}

export async function saveWalletPayoutAccounts({
  phone,
  accounts,
  primaryAccountId,
  adminNote = "",
}) {
  if (!phone) throw new Error("phone is required");
  const safeAccounts = Array.isArray(accounts) ? accounts : [];
  const primaryId = String(primaryAccountId || "");
  const primary = safeAccounts.find((a) => String(a.id || "") === primaryId) || null;

  await updateDoc(doc(db, "users", String(phone)), {
    walletPayoutAccounts: safeAccounts,
    walletPrimaryAccountId: primaryId,
    walletPayoutDetails: primary || {},
    walletPayoutAdminNote: String(adminNote || ""),
    walletUpdatedAt: serverTimestamp(),
  });
}

export function adminWalletAdjust({
  phone,
  amount,
  direction,
  note,
  idempotencyKey,
}) {
  const opType = String(direction || "").toLowerCase() === "debit" ?
    "debit_balance" :
    "add_balance";
  return adminAdjustWallet({
    phone,
    opType,
    amount,
    reason: note,
    idempotencyKey,
  });
}

export function adminWalletSetBlock({
  phone,
  blocked,
  note,
  idempotencyKey,
}) {
  return adminAdjustWallet({
    phone,
    opType: blocked ? "block" : "unblock",
    amount: 0,
    reason: note,
    idempotencyKey,
  });
}

export function adminWalletHold({
  phone,
  amount,
  direction,
  note,
  idempotencyKey,
}) {
  const opType = String(direction || "").toLowerCase() === "release" ?
    "release_hold" :
    "hold_amount";
  return adminAdjustWallet({
    phone,
    opType,
    amount,
    reason: note,
    idempotencyKey,
  });
}

export function adminWithdrawDecision({
  requestId,
  decision,
  reason,
  idempotencyKey,
}) {
  return callWalletFn("adminWithdrawDecision", {
    requestId,
    decision,
    reason: reason || "",
    idempotencyKey,
  });
}

export async function markOvertimeReviewed({
  bookingId,
  reviewed = true,
  note = "",
}) {
  if (!bookingId) throw new Error("bookingId required");
  const admin = getAdminContext();
  await updateDoc(doc(db, "artist_new_bookings", bookingId), {
    overtimeReviewed: Boolean(reviewed),
    overtimeReviewedAt: serverTimestamp(),
    overtimeReviewedBy: admin,
    overtimeAdminNote: String(note || ""),
    updatedAt: serverTimestamp(),
  });
}
